import { useCallback, useEffect, useRef, useState } from "react"
import { enqueueSnackbar } from "notistack"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { Allergen } from "../../../../admin/products/productsTypes"

import { WeekMenuActions } from "../useWeekMenuStore"
import { AiDaySettings, defaultAiSettings } from "../weekMenuAiBank"
import { WeekMenuDayState } from "../weekMenuTypes"
import { AiSettingsForDay } from "../components/ai/AiCustomizeDialog"

/** Per-day delay of the mock AI fill, so the generation looks progressive. */
const AI_DAY_FILL_DELAY_MS = 350

// TODO: call the real generation endpoint once it exists; today the dishes come from a local bank.
/** Runs the mock day-by-day AI generation and owns the review banner state. */
export function useAiGeneration(
    days: ReadonlyArray<WeekMenuDayState>,
    allergenOptions: ReadonlyArray<Allergen>,
    actions: WeekMenuActions,
) {
    const { l10n } = useAppLocalization()

    const [isWorking, setWorking] = useState(false)
    const [isBannerVisible, setBannerVisible] = useState(false)

    const timersRef = useRef<number[]>([])
    const cancelTimers = () => {
        timersRef.current.forEach((timer) => window.clearTimeout(timer))
        timersRef.current = []
    }
    useEffect(() => cancelTimers, [])

    // Cell regeneration reuses the settings last used for that day.
    const settingsByDayRef = useRef<Readonly<Record<number, AiDaySettings>>>({})
    const getAiDaySettings = useCallback(
        (dayIndex: number) => settingsByDayRef.current[dayIndex] ?? defaultAiSettings(),
        [],
    )

    // The actions identity follows template and week, so switching either drops the previous run.
    useEffect(() => {
        cancelTimers()
        setWorking(false)
        setBannerVisible(false)
        settingsByDayRef.current = {}
        actions.aiClearMarks()
    }, [actions])

    const generate = (
        settingsForDay: AiSettingsForDay,
        overwriteFilled: boolean,
        includedDayIndexes?: ReadonlySet<number>,
    ) => {
        const openDayIndexes = days
            .map((_, dayIndex) => dayIndex)
            .filter(
                (dayIndex) =>
                    !days[dayIndex].isClosed &&
                    !days[dayIndex].theme &&
                    (includedDayIndexes === undefined || includedDayIndexes.has(dayIndex)),
            )
        // Without an open day nothing would ever finish the run, leaving the page stuck as working.
        if (openDayIndexes.length === 0) {
            enqueueSnackbar({ variant: "info", message: l10n.getString("merchant-week-menu-ai-no-open-days-snackbar") })
            return
        }

        cancelTimers()
        actions.aiBegin()
        setBannerVisible(true)
        setWorking(true)
        openDayIndexes.forEach((dayIndex, order) => {
            const timer = window.setTimeout(
                () => {
                    const settings = settingsForDay(dayIndex)
                    settingsByDayRef.current = { ...settingsByDayRef.current, [dayIndex]: settings }
                    actions.aiFillDay(dayIndex, settings, allergenOptions, overwriteFilled)
                    if (order === openDayIndexes.length - 1) {
                        setWorking(false)
                        enqueueSnackbar({
                            variant: "success",
                            message: l10n.getString("merchant-week-menu-ai-ready-snackbar"),
                        })
                    }
                },
                AI_DAY_FILL_DELAY_MS * (order + 1),
            )
            timersRef.current.push(timer)
        })
    }

    const undo = () => {
        cancelTimers()
        actions.aiUndo()
        setWorking(false)
        setBannerVisible(false)
        enqueueSnackbar({ variant: "info", message: l10n.getString("merchant-week-menu-ai-reverted-snackbar") })
    }

    const closeBanner = () => {
        actions.aiClearMarks()
        setBannerVisible(false)
    }

    return { isWorking, isBannerVisible, getAiDaySettings, generate, undo, closeBanner }
}
